/* tslint:disable */

import {Injectable, Injector} from '@angular/core';
import {HttpService} from "../../http.service";
import {NgxSpinnerService} from "ngx-spinner";
import {environment} from "../../../environments/environment";
import {BehaviorSubject} from "rxjs";
import {MeshService} from "../views/three-dview/mesh.service";
import {BrainService} from "../brain.service";
import {WebGLService} from "../views/three-dview/web-gl.service";
import {NeuronsSearchService} from "../neurons/neurons-search.service";
import {ImageDrawingInfo, SelectedPosition} from "../views/two-dview/twoDViewModels";
import {TwoDService} from "../views/two-dview/two-d.service";
import {TabService} from "../../tab.service";
import {AtlasSharedService} from "../atlas-shared.service";
import {LayoutService} from "../layout/layout.service";
import {AlertService} from "../../alert.service";
import {StatusMessageService} from "../status-message.service";

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

@Injectable({
    providedIn: 'root'
})
export class RegionsService {
    urlLoadRegionsData = environment.apiURL + 'load_region_data_by_version';
    urlGetRegionsOfPosition = environment.apiURL + 'get_regions_of_position';
    urlGenerateConnectivityMatrix = environment.apiURL + 'generate_connectivity_matrix';
    urlLoadRegionAcrossVersions = environment.apiURL + 'load_region_across_versions';

    subjectIsRegionsDataLoaded = new BehaviorSubject(false);

    regionsDefaultOpacity = 0.5;
    minimumNeuronLengthInRegion = 10;

    regionsData = {
        hierarchy: {},
        dictionary: {},
        regionsVersions: [],
        selectedRegions: [],
        selectedRegionVersion: null,
        isLoaded: false,
        isSahredViewDataLoaded: false,
    };
    isSplit = false;
    regionsOfPosition = [];

    connectivityMatrix;

    selectedNewRegions = [];


    constructor(private httpService: HttpService,
                private neuronsSearchService: NeuronsSearchService,
                private meshService: MeshService,
                private brainService: BrainService,
                private webGLService: WebGLService,
                private twoDService: TwoDService,
                private atlasSharedService: AtlasSharedService,
                private spinner: NgxSpinnerService,
                private tabService: TabService,
                private injector: Injector,
                private alertService: AlertService,
                private statusMessageService: StatusMessageService) {
        document.addEventListener('keyup', this.onDocumentKeyUp.bind(this), false);


    }

    loadRegionsData(isSpinnerShown = true, regionVersion = this.atlasSharedService.defaultRegionVersion) {
        if (!this.regionsData.isLoaded) {
            if (isSpinnerShown) {
                this.spinner.show();
            }
            this.httpService.postRequest(this.urlLoadRegionsData, {
                regionVersion: regionVersion
            }).subscribe((response) => {
                this.regionsData.hierarchy = response['data']['regions_hierarchy']; // The hierarchy of the selected version, the default is V2.0
                this.regionsData.dictionary = response['data']['regions_dictionary']; // All Regions of all versions
                this.regionsData.regionsVersions = response['data']['regions_versions'];
                this.regionsData.selectedRegionVersion = response['data']['region_version_data'];
                this.regionsData.isLoaded = true;
                this.subjectIsRegionsDataLoaded.next(this.regionsData.isLoaded);

                // This function ensures that regions on the left side are selected based on the right side menu
                // when switching between regions. It was added to maintain selection consistency, preventing the loss of pointers to the default loaded regions.
                this.updateSelectedAndVisibleRegions();

                if (isSpinnerShown) {
                    this.spinner.hide();
                }
            });
        }
    }

    updateSelectedAndVisibleRegions() {
        if (this.regionsData.selectedRegions.length) {
            for (let i = 0; i < this.regionsData.selectedRegions.length; i++) {
                let regionID = this.regionsData.selectedRegions[i].id;
                this.regionsData.dictionary[regionID].selected = true;
                this.regionsData.dictionary[regionID].show = true;
            }
        }
    }

    onRegionSelectionChanged(regionID, isSpinnerShown = true) {

        if (isSpinnerShown) {
            this.spinner.show();
        }
        this.tabService.openRegionsTab();
        let region = this.regionsData.dictionary[regionID];
        if (region.selected) {
            this.selectRegion(region).then(r =>
                setTimeout(() => {
                    this.openRegionPanelGroup(regionID);
                }, 1000)
            );
        } else {
            this.deselectRegion(region, true);
        }
        this.twoDService.updateScene();

    }

    openRegionPanelGroup(currentSelectedRegionID) {
        for (let i = 0; i < this.regionsData.selectedRegions.length; i++) {
            let region = this.regionsData.selectedRegions[i];
            let arrowElement = document.getElementById('regionPanelArrow' + currentSelectedRegionID.toString());
            // to open current selected panel group
            if (region.id === currentSelectedRegionID) {
                if (!arrowElement.classList.contains('in')) {
                    arrowElement.click();
                }
            } else {
                //To close other regon panel groups
                if (arrowElement.classList.contains('in')) {
                    arrowElement.click();
                }
            }
        }
    }

    async selectRegion(region) {
        region.selected = true;
        region.show = true;

        this.initRegion2DDrawingProperties(region);

        await this.initRegion3DMesh(region);

        // Check if the region is already in selectedRegions
        const regionExists = this.regionsData.selectedRegions.some(
            selectedRegion => selectedRegion.id === region.id
        );

        if (!regionExists) {
            this.regionsData.selectedRegions.push(region);
            this.addRegionTo3DScene(region, false);
            this.addRegionTo2DScene(region);
        }

    }

    // find the index of a region in selectedRegions based on id and version.number
    findRegionIndex(region) {
        return this.regionsData.selectedRegions.findIndex(selectedRegion =>
            selectedRegion.id === region.id &&
            selectedRegion.version.number === region.version.number
        );
    }

    deselectRegion(region, isUpdate) {
        let regionID = region.id;
        this.regionsData.dictionary[regionID].selected = false;
        this.regionsData.dictionary[regionID].show = false;
        // region.selected = false;
        // region.show = false;

        // const index = this.regionsData.selectedRegions.indexOf(region);
        const index = this.findRegionIndex(region);
        this.regionsData.selectedRegions.splice(index, 1);

        this.removeRegionFrom3DScene(region);
        this.removeRegionFrom2DScene(region, isUpdate);
    }

    initRegion2DDrawingProperties(region) {
        if (region.drawingInfo === undefined || region.drawingInfo === null) {
            region.drawingInfo = new ImageDrawingInfo(region.visualization_data._2d, region.visualization_data.color, true);
            if (this.atlasSharedService.sharingCurrentViewStatus.hash) {
                let index = region['shareViewIndexOfRegion']
                if (this.atlasSharedService.sharedViewItems.regions.onlyOutline[index]) {
                    region.drawingInfo.isOutline = true;
                    setTimeout(() => {
                        this.twoDService.updateScene();
                    }, 400);
                }
            }


        }
    }

    async initRegion3DMesh(region) {
        if (region.mesh === undefined || region.mesh === null) {
            region.mesh = await this.meshService.createMesh(region.visualization_data._3d.mesh_file, region.visualization_data.color,
                this.regionsDefaultOpacity, this.brainService.brain3DModel.center);
            if (this.atlasSharedService.sharingCurrentViewStatus.hash) {
                let index = region['shareViewIndexOfRegion']
                if (!!this.atlasSharedService.sharedViewItems.regions.opacity[index]) {
                    region.mesh.material.opacity = this.atlasSharedService.sharedViewItems.regions.opacity[index];
                } else {
                    region.mesh.material.opacity = this.regionsDefaultOpacity;
                }

            }
        }
    }

    addRegionTo2DScene(region) {
        // this.twoDService.addImageTo2DScene(this.isSplit, region.drawingInfo);
        this.twoDService.addNonSplitImageTo2DScene(region.drawingInfo);
    }

    addRegionTo3DScene(region, isSpinnerShown = true) {
        if (this.brainService.brainModelsVariables._3D.brainGroup.children.indexOf(region.mesh) === -1) {
            this.brainService.brainModelsVariables._3D.brainGroup.add(region.mesh);
            this.webGLService.mouseIntersections.push(region.mesh);
        }
        if (isSpinnerShown) {
            this.spinner.hide();
        }
    }

    removeRegionFrom2DScene(region, isUpdate) {
        // this.twoDService.removeImageFrom2DScene(this.isSplit, region.drawingInfo, isUpdate);
        this.twoDService.removeNonSplitImageFrom2DScene(region.drawingInfo, isUpdate);
    }

    deselectAllRegions() {
        while (this.regionsData.selectedRegions.length > 0) {
            this.deselectRegion(this.regionsData.selectedRegions[0], false);
        }
        this.twoDService.updateScene();
    }

    removeRegionFrom3DScene(region) {
        this.brainService.brainModelsVariables._3D.brainGroup.remove(region.mesh);
        this.webGLService.mouseIntersections.splice(this.webGLService.mouseIntersections.indexOf(region.mesh), 1);
    }

    searchForNeurons(regionID: number, searchOption: number) {
        this.neuronsSearchService.searchForNeuronsInRegion(regionID, searchOption, this.minimumNeuronLengthInRegion);
    }

    onDocumentKeyUp(event) {

        if (event.target.type === 'text') {
            return;
        }

        if (event.key === 'b') {
            if (event.target.tagName.toUpperCase() !== 'INPUT' && event.target.tagName.toUpperCase() !== 'TEXTAREA') {
                this.atlasSharedService.onHidePanelMovementOptions(true, false, false, false, true)
                this.getRegionsOfPosition(SelectedPosition.coordinates);
            }
        }

    }

    requestRegionsOfPosition(position) {
        this.httpService.postRequest(this.urlGetRegionsOfPosition, {position: position}).subscribe((response) => {
            this.regionsOfPosition = [];
            for (let i = 0; i < response.regionsIDs.length; i++) {
                this.regionsOfPosition.push(this.regionsData.dictionary[response.regionsIDs[i]]);
            }
        });
    }

    getRegionsOfPosition(position) {
        if (!this.regionsData.isLoaded) {
            this.loadRegionsData(true, this.atlasSharedService.defaultRegionVersion);

            this.subjectIsRegionsDataLoaded.subscribe(isRegionsDataLoaded => {
                if (isRegionsDataLoaded === true) {
                    this.requestRegionsOfPosition(position);
                }
            });
        } else {
            this.requestRegionsOfPosition(position);
        }
    }

    generateConnectivityMatrix() {
        this.httpService.postRequest(this.urlGenerateConnectivityMatrix, {}).subscribe((response) => {
            this.connectivityMatrix = response.connectivityMatrix;
        });
    }

    getSelectedRegionsDataShareView() {
        let data = [];
        for (let i = 0; i < this.regionsData.selectedRegions.length; i++) {
            let region = this.regionsData.selectedRegions[i];
            if (region.show) {
                data.push({
                    id: region.id,
                    order: i,
                    color: region.drawingInfo.color,
                    isOutline: region.drawingInfo.isOutline,
                    opacity: region.mesh.material.opacity,
                })
            }
        }
        return data;
    }

    async loadSelectedRegionsShareView(regionsResponse) {
        console.log("SHARE CURRENT VIEW ==> Start loading selected regions");
        let layoutService = this.injector.get<LayoutService>(LayoutService);
        return new Promise<void>(async (resolve, reject) => {
            let ids = regionsResponse['ids'];
            let colors = regionsResponse['colors'];
            this.atlasSharedService.sharedViewItems.regions.opacity = regionsResponse['opacity'];
            this.atlasSharedService.sharedViewItems.regions.onlyOutline = regionsResponse['only_outline'];

            if (ids.length) {
                this.tabService.openRegionsTab();
                await this.statusMessageService.updateStatus("Start loading regions", true,true,2000);
                if (!this.regionsData.isLoaded) {
                    this.loadRegionsData(false);
                    this.subjectIsRegionsDataLoaded.subscribe(async isRegionsDataLoaded => {
                        if (isRegionsDataLoaded === true) {
                            if (!this.regionsData.isSahredViewDataLoaded) {
                                for (let i = 0; i < ids.length; i++) {
                                    let regionID = ids[i];
                                    this.regionsData.dictionary[regionID].selected = true;
                                    this.regionsData.dictionary[regionID].visualization_data.color = colors[i];
                                    this.regionsData.dictionary[regionID]['shareViewIndexOfRegion'] = i;
                                    this.onRegionSelectionChanged(regionID, true);
                                    await delay(3000);

                                    layoutService.updateSharCurrentViewScene();
                                    this.regionsData.isSahredViewDataLoaded = true;
                                }
                            }
                        }

                    });

                    this.twoDService.updateScene();
                }


            }
            resolve();
            // Perform the asynchronous task
            // Call resolve() when the task is complete, or reject(error) if there's an error
        });


    }

    isSelectedRegionsHidden(selectedRegionsLength) {
        let regionsLength = selectedRegionsLength;
        for (let j = 0; j < selectedRegionsLength; j++) {
            if (!this.regionsData.selectedRegions[j].show) {
                regionsLength -= 1;
            }
        }
        return regionsLength == 0;
    }

    loadRegionAcrossVersions(region) {
        this.httpService.postRequest(this.urlLoadRegionAcrossVersions, {
            regionName: region.name,
            regionVersion: region.version.number,
        }).subscribe((response) => {
            if (response.success) {
                if (response.regionsID.length) {
                    for (let i = 0; i < response.regionsID.length; i++) {
                        let regionID = response.regionsID[i];
                        let region = this.regionsData.dictionary[regionID];
                        region.selected = true;
                        region.show = true;
                        this.onRegionSelectionChanged(regionID);
                    }
                } else {
                    this.alertService.showWarningMsg("No regions detected.");
                }
            } else {
                this.alertService.showErrorMsg('SOMETHING_WENT_WRONG');
            }


        });

    }


    searchForRegionsByVersion(selectedRegionVersion) {
        this.regionsData.selectedRegionVersion = selectedRegionVersion;
        this.regionsData.isLoaded = false;
        this.loadRegionsData(true, selectedRegionVersion['display_name']);
    }
}
